import { format, addHours } from "date-fns";
import { toZonedTime } from "date-fns-tz";

export const formatDateUTC = (isoDate: string): string => {
  const utcOffset = -6; // UTC-6
  const adjustedDate = addHours(new Date(isoDate), utcOffset); // Ajustar a UTC-6
  return format(adjustedDate, "dd/MM/yyyy"); // Formatear la fecha
};

export const formatDateUtil = (isoDate: string): string => {
  //console.log('Fecha recibida para convertir', isoDate);
  //const date = new Date(isoDate.substring(0,10));
  const zoneDate = toZonedTime(isoDate, "UTC");
  //console.info('Fecha convertida: ', zoneDate);
  return format(zoneDate, "dd/MM/yyyy"); // Formatear la fecha
};

export const getLocalDateString = (): string => {
  const today = new Date(); // Hora local
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, "0");
  const day = String(today.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`; // "YYYY-MM-DD"
};

export const formatDate = (dateInput: Date | string): string => {
  try {
    const date =
      typeof dateInput === "string" ? new Date(dateInput) : dateInput;

    if (isNaN(date.getTime())) {
      return "Fecha inválida";
    }

    return date.toLocaleDateString("es-ES", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  } catch (error) {
    console.error("Error formateando fecha:", error);
    return "Fecha inválida";
  }
};

export const formatTimeFromISO = (dateString: string): string => {
  // Elimina la Z y extrae solo HH:MM
  const timePart = dateString.split("T")[1]; // "16:00:00.000Z"
  return timePart.substring(0, 5); // "16:00"
};

export const formatDateToText = (
  dateString: string | null | undefined,
): string => {
  if (!dateString) return "Fecha no disponible";

  try {
    // Parsear la fecha manualmente para evitar problemas de zona horaria
    const dateParts = dateString.split("-");
    if (dateParts.length !== 3) {
      // Si no es formato YYYY-MM-DD, intentar parsear como Date
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return "Fecha inválida";
      }
      return formatDateManual(date);
    }

    const year = parseInt(dateParts[0], 10);
    const month = parseInt(dateParts[1], 10) - 1; // Mes 0-based
    const day = parseInt(dateParts[2], 10);

    const date = new Date(year, month, day);
    return formatDateManual(date);
  } catch (error) {
    console.error("Error formateando fecha:", error);
    return "Error de formato";
  }
};

// Función auxiliar para formatear fecha manualmente
const formatDateManual = (date: Date): string => {
  const days = [
    "Domingo",
    "Lunes",
    "Martes",
    "Miércoles",
    "Jueves",
    "Viernes",
    "Sábado",
  ];
  const months = [
    "enero",
    "febrero",
    "marzo",
    "abril",
    "mayo",
    "junio",
    "julio",
    "agosto",
    "septiembre",
    "octubre",
    "noviembre",
    "diciembre",
  ];

  const dayOfWeek = days[date.getDay()];
  const dayOfMonth = date.getDate();
  const monthName = months[date.getMonth()];
  const year = date.getFullYear();

  return `${dayOfWeek}, ${dayOfMonth} de ${monthName} de ${year}`;
};

/**
 * Obtiene la fecha y hora actual en formato datetime-local (YYYY-MM-DDTHH:mm)
 * Compatible con input type="datetime-local"
 */
export const getCurrentDateTimeLocal = (): string => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  const hours = String(now.getHours()).padStart(2, "0");
  const minutes = String(now.getMinutes()).padStart(2, "0");

  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

/**
 * Formatea una fecha a formato datetime-local (YYYY-MM-DDTHH:mm)
 */
export const formatToDateTimeLocal = (date: Date | string): string => {
  const d = typeof date === "string" ? new Date(date) : date;

  if (isNaN(d.getTime())) {
    throw new Error("Fecha inválida");
  }

  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  const hours = String(d.getHours()).padStart(2, "0");
  const minutes = String(d.getMinutes()).padStart(2, "0");

  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

/**
 * Valida que una fecha no sea anterior a la actual
 */
export const isDateNotInPast = (date: Date | string): boolean => {
  const inputDate = typeof date === "string" ? new Date(date) : date;
  const now = new Date();

  return inputDate >= now;
};

/**
 * Calcula la fecha mínima de fin (1 hora después de la fecha de inicio)
 */
export const getMinEndDate = (startDate: Date | string): string => {
  const start = typeof startDate === "string" ? new Date(startDate) : startDate;
  const minEnd = new Date(start.getTime() + 60 * 60 * 1000); // +1 hora

  return formatToDateTimeLocal(minEnd);
};

/**
 * Parsea una fecha/hora en formato local (sin conversión UTC)
 * Útil para fechas que vienen de BD en formato "YYYY-MM-DD HH:MM:SS"
 */
export const parseLocalDateTime = (
  dateTimeString: string | null,
): Date | null => {
  if (!dateTimeString) return null;

  console.log("parseLocalDateTime recibe:", dateTimeString);

  // Formato ISO con T: "2026-03-05T07:59:10.668Z"
  const isoMatch = dateTimeString.match(
    /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})/,
  );

  if (isoMatch) {
    const [_, year, month, day, hour, minute, second] = isoMatch;
    // Crear fecha LOCAL ignorando completamente la Z
    // Esto interpreta "07:59:10" como 7:59am en Costa Rica (local)
    return new Date(
      parseInt(year),
      parseInt(month) - 1,
      parseInt(day),
      parseInt(hour),
      parseInt(minute),
      parseInt(second),
    );
  }

  // Formato BD (por si acaso): "2026-03-05 07:59:10"
  const bdMatch = dateTimeString.match(
    /^(\d{4})-(\d{2})-(\d{2})\s+(\d{2}):(\d{2}):(\d{2})/,
  );

  if (bdMatch) {
    const [_, year, month, day, hour, minute, second] = bdMatch;
    return new Date(
      parseInt(year),
      parseInt(month) - 1,
      parseInt(day),
      parseInt(hour),
      parseInt(minute),
      parseInt(second),
    );
  }

  return null;
};

/**
 * Calcula la duración entre un checkin y ahora (o checkout)
 */
export const calculateDuration = (
  checkin: string | null,
  checkout: string | null = null,
): string => {
  console.info("checkin en CalculateDuration", checkin);

  if (!checkin) return "-";

  const checkinDate = parseLocalDateTime(checkin);
  if (!checkinDate) return "-";

  const endDate = checkout ? parseLocalDateTime(checkout) : new Date(); // Hora actual

  if (!endDate) return "-";

  const durationMs = endDate.getTime() - checkinDate.getTime();
  const durationMins = Math.floor(durationMs / 60000);

  if (durationMins < 0) return "0 min";
  if (durationMins < 60) return `${durationMins} min`;

  const hours = Math.floor(durationMins / 60);
  const mins = durationMins % 60;
  return `${hours}h ${mins}m`;
};

/**
 * Obtiene el tiempo transcurrido desde checkin en minutos
 */
export const getElapsedMinutes = (checkin: string | null): number => {
  if (!checkin) return 0;

  const checkinDate = parseLocalDateTime(checkin);
  if (!checkinDate) return 0;

  const now = new Date();
  const durationMs = now.getTime() - checkinDate.getTime();
  return Math.floor(durationMs / 60000);
};
